/**
 * <s-video-reel> — Muted, looping showcase video that plays while visible.
 *
 * Plays its native <video> when at least half of the card is on screen and
 * pauses it when scrolled away. Respects prefers-reduced-motion by never
 * starting playback automatically. A shopper's explicit pause sticks until
 * they press play again.
 *
 * Commands (dispatched via commandfor/command on descendant buttons):
 *   --toggle-play  — Play or pause the video.
 *   --toggle-sound — Mute or unmute the video. Unmuting mutes other reels.
 *
 * Required child markup:
 *   video                       — The Shopify-hosted video (muted, loop, playsinline).
 *   [data-play-toggle]          — Play/pause button; its aria-label and icon state are updated.
 *   [data-sound-toggle]         — Sound button; its aria-label and icon state are updated.
 *
 * Element dataset (on <s-video-reel> itself):
 *   data-label-play, data-label-pause, data-label-mute, data-label-unmute — translated labels.
 */
class SVideoReel extends HTMLElement {
  #ac;
  #observer;
  #video;
  #playButton;
  #soundButton;
  #userPaused = false;
  #visible = false;

  connectedCallback() {
    this.#video = this.querySelector('video');
    this.#playButton = this.querySelector('[data-play-toggle]');
    this.#soundButton = this.querySelector('[data-sound-toggle]');
    if (!this.#video) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#video.muted = true;
    this.#video.loop = true;
    this.#video.playsInline = true;
    this.#video.setAttribute('playsinline', '');
    this.#video.removeAttribute('controls');

    this.addEventListener('command', (event) => {
      if (event.command === '--toggle-play') this.#togglePlay();
      if (event.command === '--toggle-sound') this.#toggleSound();
    }, opts);

    this.#video.addEventListener('play', () => this.#syncPlayButton(), opts);
    this.#video.addEventListener('pause', () => this.#syncPlayButton(), opts);
    this.#video.addEventListener('volumechange', () => this.#syncSoundButton(), opts);

    document.addEventListener('s-video-reel:unmuted', (event) => {
      if (event.target !== this && !this.#video.muted) this.#video.muted = true;
    }, opts);

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) this.#userPaused = true;

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        this.#visible = entry.isIntersecting;
        if (this.#visible && !this.#userPaused) {
          this.#play();
        } else if (!this.#visible) {
          this.#video.pause();
        }
      }
    }, { threshold: 0.5 });
    this.#observer.observe(this);

    this.#syncPlayButton();
    this.#syncSoundButton();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #play() {
    const attempt = this.#video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => this.#syncPlayButton());
  }

  #togglePlay() {
    if (this.#video.paused) {
      this.#userPaused = false;
      this.#play();
    } else {
      this.#userPaused = true;
      this.#video.pause();
    }
  }

  #toggleSound() {
    this.#video.muted = !this.#video.muted;
    if (!this.#video.muted) {
      this.dispatchEvent(new CustomEvent('s-video-reel:unmuted', { bubbles: true }));
      if (this.#video.paused) {
        this.#userPaused = false;
        this.#play();
      }
    }
  }

  #syncPlayButton() {
    if (!this.#playButton) return;
    const paused = this.#video.paused;
    this.#playButton.setAttribute('aria-label', paused ? this.dataset.labelPlay : this.dataset.labelPause);
    this.#playButton.toggleAttribute('data-paused', paused);
  }

  #syncSoundButton() {
    if (!this.#soundButton) return;
    const soundOn = !this.#video.muted;
    this.#soundButton.setAttribute('aria-label', soundOn ? this.dataset.labelMute : this.dataset.labelUnmute);
    this.#soundButton.toggleAttribute('data-sound-on', soundOn);
  }
}

if (!customElements.get('s-video-reel')) customElements.define('s-video-reel', SVideoReel);
